import { motion, AnimatePresence } from 'motion/react';
import { useState, useCallback } from 'react';
import useCopy from '../util/useCopy';
import BellIcon from './BellIcon';
import CheckCircleIcon from './CheckCircleIcon';
import CoffeeIcon from './CoffeeIcon';
import ArrowUpRightIcon from './ArrowUpRightIcon';
import StarIcon from './StarIcon';
import CloseIcon from './CloseIcon';
import { PopWrapper } from './PopWrapper';
import { makeClassName } from '@siteless/util';
import CurrentUserResource, {
  CurrentUserResourceType,
} from '../resources/Auth/CurrentUser';
import { buyMeACoffeeUrl, thisOscarsYear } from '../config/constants';
import { consentToNotificationForYearMutation } from '../resources/Auth/mutations';
import { getUserWillReceiveNotificationForYear } from '../util/getEffectiveNotificationConsent';
import { BrowserStorageResource } from '../resources/BrowserStorage';
import { writeBrowserStorage } from '../resources/writeBrowserStorage';
import getWrapMessage from '../util/getWrapMessage';

const yearOfInterest = thisOscarsYear + 1;

const WrapBannerContent = ({
  currentUser,
  delayShowMs = 0,
  onDismiss,
}: {
  currentUser: CurrentUserResourceType;
  delayShowMs: number;
  onDismiss: () => void;
}) => {
  let copy = useCopy();
  let [reminderOn, setReminderOn] = useState(() =>
    getUserWillReceiveNotificationForYear(currentUser, yearOfInterest),
  );
  let [coffeeExpanded, setCoffeeExpanded] = useState(false);
  let [dismissed, setDismissed] = useState(false);

  const handleClose = useCallback(() => {
    setDismissed(true);
  }, []);

  const handleNotificationToggle = useCallback(() => {
    let next = !reminderOn;
    consentToNotificationForYearMutation({
      consentStatus: next,
      userId: currentUser.id,
      year: yearOfInterest,
    });
    setReminderOn(next);
  }, [currentUser, reminderOn]);

  return (
    <AnimatePresence onExitComplete={onDismiss}>
      {!dismissed && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{
            height: 0,
            opacity: 0,
            transition: { type: 'spring', stiffness: 300, damping: 30 },
          }}
          transition={{
            type: 'spring',
            stiffness: 200,
            damping: 25,
            delay: delayShowMs / 1000,
          }}
          className="overflow-hidden"
        >
          <div className="px-3 pt-2">
            <div className="rounded-xl bg-purple-900 text-neutral-0 sm:mx-auto sm:w-96">
              <div className="flex items-start gap-1 px-4 pt-3 mb-2">
                <div className="flex-1 flex flex-col gap-0.5">
                  <div className="text-sm font-medium">
                    {copy["That's a wrap!"]({ year: thisOscarsYear })}
                  </div>
                  <div className="flex items-center gap-1.5 text-sm text-purple-300">
                    <StarIcon className="size-3 flex-none" />
                    {getWrapMessage(currentUser, copy)}
                  </div>
                </div>
                <PopWrapper
                  className="-mr-2.5 -mt-1.5 flex size-7 flex-none cursor-pointer items-center justify-center rounded-full bg-rose-default"
                  onClick={handleClose}
                >
                  <CloseIcon className="size-4 fill-current text-neutral-0" />
                </PopWrapper>
              </div>
              <div className="px-3 pb-2.5">
                <div className="flex items-stretch gap-2">
                  <PopWrapper
                    className={makeClassName(
                      'relative flex min-w-0 cursor-pointer items-center gap-2 overflow-hidden rounded-full border border-purple-800 py-1.5 text-sm font-medium transition-all duration-300 ease-out',
                      coffeeExpanded ? 'flex-[0_0_36px] px-2.5' : 'flex-1 px-4',
                    )}
                    onClick={() => {
                      if (coffeeExpanded) {
                        setCoffeeExpanded(false);
                      } else {
                        handleNotificationToggle();
                      }
                    }}
                  >
                    {/* Off state (sits behind) */}
                    <BellIcon className="h-4 w-4 flex-none text-rose-brightOnPurple" />
                    <span
                      className={makeClassName(
                        'flex-1 whitespace-nowrap text-center text-rose-brightOnPurple transition-opacity duration-150',
                        coffeeExpanded ? 'opacity-0' : 'opacity-100',
                      )}
                    >
                      {copy.wrapTurnOnReminder}
                    </span>
                    {/* On state — wipes in from left */}
                    <motion.div
                      className={makeClassName(
                        'absolute inset-0 flex items-center gap-2 rounded-full bg-purple-800 transition-[padding] duration-300 ease-out',
                        coffeeExpanded ? 'px-2.5' : 'px-4',
                      )}
                      initial={false}
                      animate={{
                        clipPath: reminderOn
                          ? 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)'
                          : 'polygon(0% 0%, 0% 0%, -15% 100%, -15% 100%)',
                      }}
                      transition={{
                        type: 'spring',
                        stiffness: 400,
                        damping: 30,
                      }}
                    >
                      <motion.div
                        className="flex-none"
                        initial={false}
                        animate={{ scale: reminderOn ? 1 : 0 }}
                        transition={{
                          type: 'spring',
                          stiffness: 400,
                          damping: 20,
                          delay: reminderOn ? 0.15 : 0,
                        }}
                      >
                        <CheckCircleIcon className="h-4 w-4 text-white" />
                      </motion.div>
                      <span className="flex-1 whitespace-nowrap text-center text-purple-300">
                        {copy.wrapReminderOn({ year: yearOfInterest })}
                      </span>
                    </motion.div>
                  </PopWrapper>
                  <PopWrapper
                    className={makeClassName(
                      'flex min-w-0 cursor-pointer items-center gap-2 overflow-hidden rounded-full border border-purple-800 py-1.5 text-sm font-medium text-white transition-all duration-300 ease-out',
                      coffeeExpanded ? 'flex-1 px-4' : 'flex-[0_0_36px] px-2.5',
                    )}
                    onClick={() => {
                      if (coffeeExpanded) {
                        window.open(buyMeACoffeeUrl);
                      } else {
                        setCoffeeExpanded(true);
                      }
                    }}
                  >
                    <CoffeeIcon className="h-4 w-4 flex-none" />
                    <span
                      className={makeClassName(
                        'flex-1 whitespace-nowrap text-center underline decoration-dotted transition-opacity duration-150',
                        coffeeExpanded ? 'opacity-100' : 'opacity-0',
                      )}
                    >
                      {copy.wrapBuyMeACoffee}
                    </span>
                    <div
                      className={makeClassName(
                        'ml-auto flex-none text-purple-500 transition-opacity duration-150',
                        coffeeExpanded ? 'opacity-100' : 'opacity-0',
                      )}
                    >
                      <ArrowUpRightIcon className="size-4" />
                    </div>
                  </PopWrapper>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export let WrapBanner = ({ delayShowMs }: { delayShowMs: number }) => {
  const currentUser = CurrentUserResource.use();
  const browserStorage = BrowserStorageResource.use();

  if (!currentUser) return null;
  if (browserStorage?.dismissedWrapBannerForYears?.[String(thisOscarsYear)])
    return null;

  const handleDismiss = () => {
    writeBrowserStorage((last) => ({
      ...last,
      dismissedWrapBannerForYears: {
        ...(last?.dismissedWrapBannerForYears || {}),
        [String(thisOscarsYear)]: true,
      },
    }));
  };

  return (
    <WrapBannerContent
      currentUser={currentUser}
      delayShowMs={delayShowMs}
      onDismiss={handleDismiss}
    />
  );
};
